/**
 * 폼 관련 공통 유틸리티 함수들
 */

/**
 * 폼 검증 규칙을 생성하는 함수
 * 
 * @param fieldName - 필드명
 * @param required - 필수 여부 (기본값: true)
 * @param minLength - 최소 길이 (선택사항)
 * @param maxLength - 최대 길이 (선택사항)
 * @param pattern - 정규식 패턴 (선택사항)
 * @returns {Array} Ant Design Form 검증 규칙 배열
 */
export const createValidationRule = (
  fieldName: string,
  required: boolean = true,
  minLength?: number,
  maxLength?: number,
  pattern?: RegExp
) => {
  const rules: any[] = [];

  if (required) {
    rules.push({
      required: true,
      message: `${fieldName}을(를) 입력해주세요.`,
    });
  }

  if (minLength) {
    rules.push({
      min: minLength,
      message: `${fieldName}은(는) 최소 ${minLength}자 이상이어야 합니다.`,
    });
  }

  if (maxLength) {
    rules.push({
      max: maxLength,
      message: `${fieldName}은(는) 최대 ${maxLength}자까지 입력 가능합니다.`,
    });
  }

  if (pattern) {
    rules.push({
      pattern,
      message: `${fieldName} 형식이 올바르지 않습니다.`,
    });
  }

  return rules;
};

// 공통 검증 규칙들
export const validationRules = {
  // 사용자명 검증 규칙
  username: createValidationRule('아이디', true, 3, 50),
  
  // 이메일 검증 규칙
  email: [
    { required: true, message: '이메일을 입력해주세요.' },
    { type: 'email' as const, message: '올바른 이메일 형식이 아닙니다.' }
  ],
  
  // 비밀번호 검증 규칙
  password: createValidationRule('비밀번호', true, 6, 100),
  
  // 비밀번호 확인 검증 규칙
  confirmPassword: (passwordField: string = 'password') => [
    { required: true, message: '비밀번호 확인을 입력해주세요.' },
    ({ getFieldValue }: any) => ({
      validator(_: any, value: string) {
        if (!value || getFieldValue(passwordField) === value) {
          return Promise.resolve();
        }
        return Promise.reject(new Error('비밀번호가 일치하지 않습니다.'));
      },
    }),
  ],
  
  // 제목 검증 규칙
  title: createValidationRule('제목', true, 1, 200),
  
  // 내용 검증 규칙
  content: createValidationRule('내용', true, 1),
  
  // 법명 검증 규칙
  bubmyeng: createValidationRule('법명', false, 0, 100),
  
  // 소속 검증 규칙
  organ_name: createValidationRule('소속', false, 0, 100),
  
  // 역할 검증 규칙
  role: [{ required: true, message: '역할을 선택해주세요.' }],
  
  // 분류 검증 규칙
  category: [{ required: true, message: '분류를 선택해주세요.' }],
  
  // 상태 검증 규칙
  status: [{ required: true, message: '상태를 선택해주세요.' }],
};

/**
 * 폼 초기값을 설정하는 함수
 * 
 * @param form - Ant Design Form 인스턴스
 * @param initialValues - 초기값 객체
 */
export const setFormInitialValues = (form: any, initialValues: any) => {
  if (initialValues) {
    form.setFieldsValue(initialValues);
  }
};

/**
 * 폼을 리셋하는 함수
 * 
 * @param form - Ant Design Form 인스턴스
 */
export const resetForm = (form: any) => {
  form.resetFields();
};

/**
 * 폼 유효성 검사를 수행하는 함수
 * 
 * @param form - Ant Design Form 인스턴스
 * @returns {Promise<boolean>} 유효성 검사 결과
 */
export const validateForm = async (form: any): Promise<boolean> => {
  try {
    await form.validateFields();
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * 폼 필드 에러를 설정하는 함수
 * 
 * @param form - Ant Design Form 인스턴스
 * @param fieldName - 필드명
 * @param errorMessage - 에러 메시지
 */
export const setFieldError = (form: any, fieldName: string, errorMessage: string) => {
  form.setFields([
    {
      name: fieldName,
      errors: [errorMessage],
    },
  ]);
};

/**
 * 폼 필드 에러를 초기화하는 함수
 * 
 * @param form - Ant Design Form 인스턴스
 * @param fieldName - 필드명 (선택사항, 없으면 모든 필드)
 */
export const clearFieldError = (form: any, fieldName?: string) => {
  if (fieldName) {
    form.setFields([
      {
        name: fieldName,
        errors: [],
      },
    ]);
  } else {
    form.setFields(
      Object.keys(form.getFieldsValue()).map(key => ({
        name: key,
        errors: [],
      }))
    );
  }
}; 